import { Box, Chip, Paper, Stack, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { RatedEnergyRow } from '@/features/resource/configuration/components/RatedEnergyRow'
import type { ResourceLocalConfigFormValues } from '@/features/resource/configuration/utils/resourceLocalConfig'
import { useBatteryInfoQuery } from '@/features/resource/hooks/useBatteryInfoQuery'
import { formatEnergyWh, isReportedEnergyWh } from '@/utils/powerEnergyTransformations'

type OperatingEnvelopeChartProps = {
  batteryControlPortId: string | null
}

/**
 * Theme-tokenized chart palette. Built once per render from the MUI theme so the operating /
 * trickle / setpoint colours track the whitelabel `palette.success` + `palette.warning`, and the
 * neutral grid / axis / label inks track `palette.divider` + `palette.text.*`. Single shape used
 * by BOTH the JSX legend chips and the SVG body so the two can't drift on edit. `alpha()` from
 * MUI produces the semi-transparent fills the original literals encoded as `rgba(..., 0.18)`.
 */
type ChartColors = {
  operating: { stroke: string; fill: string; label: string }
  trickle: { stroke: string; fill: string; label: string }
  grid: string
  axisLine: string
  axisLabel: string
  axisTitle: string
}

function buildChartColors(theme: Theme): ChartColors {
  return {
    operating: {
      stroke: theme.palette.success.main,
      fill: alpha(theme.palette.success.main, 0.18),
      label: theme.palette.success.dark,
    },
    trickle: {
      stroke: theme.palette.warning.main,
      fill: alpha(theme.palette.warning.main, 0.18),
      label: theme.palette.warning.dark,
    },
    grid: theme.palette.divider,
    axisLine: theme.palette.text.disabled,
    axisLabel: theme.palette.text.secondary,
    axisTitle: theme.palette.text.primary,
  }
}

/**
 * Right-column sticky chart on the Resource configuration tab. Subscribes to live form values via
 * `useWatch` and re-renders as the user edits limits / trickle, so the operating envelope is always
 * a faithful preview of what would be saved.
 *
 * The rated-energy row above the plot states that value in real text, which is also the only
 * accessible route to it: the SVG is a single `role="img"` with a static label, so its tick `<text>`
 * nodes are invisible to assistive tech. Labelling the ticks properly is a separate follow-up.
 *
 * An energy axis is overlaid on top of the percentage SoC axis when `ratedEnergy` is available from
 * the battery's telemetry endpoint; without it the plot degrades to percentage-only labels.
 *
 * A failed FIRST fetch is surfaced twice: the rated-energy row says "Could not be read" inline, and
 * `useBatteryInfoQuery` raises the app's generic error alert. A failed REFETCH is surfaced once — the
 * row yields to the value TanStack retained (see `RatedEnergyRow`), so the alert is the only signal.
 * Keeping the query here rather than in the row is what holds this at two rather than three:
 * `useErrorHandler` fires per consumer and has no opt-out.
 */
export function OperatingEnvelopeChart({ batteryControlPortId }: Readonly<OperatingEnvelopeChartProps>) {
  const { t } = useTranslation()
  const theme = useTheme()
  const colors = useMemo(() => buildChartColors(theme), [theme])
  const { control } = useFormContext<ResourceLocalConfigFormValues>()

  const [
    maxSocPercent,
    minSocPercent,
    maxChargePowerKw,
    maxDischargePowerKw,
    targetSocEnabled,
    targetSocPercent,
    trickleChargePowerKw,
    trickleDischargePowerKw,
  ] = useWatch({
    control,
    name: [
      'maxSocPercent',
      'minSocPercent',
      'maxChargePowerKw',
      'maxDischargePowerKw',
      'targetSocEnabled',
      'targetSocPercent',
      'trickleChargePowerKw',
      'trickleDischargePowerKw',
    ],
  }) as [
    number | null,
    number | null,
    number | null,
    number | null,
    boolean,
    number | null,
    number | null,
    number | null,
  ]

  // `BatteryInfoResponse.ratedEnergy` is in **Wh**, not kWh: the battery reports it in Wh and it is
  // a pass-through at every hop from there to this response, so a 2500 kWh battery arrives as
  // 2_500_000. See the provenance note on `BatteryInfoResponse` for the path it travels. Gated on
  // the resource actually having a battery control port (CLOUD / non-battery resources pass null).
  const { batteryInfo, isError, isLoading } = useBatteryInfoQuery(batteryControlPortId)
  const ratedEnergyWh = batteryInfo?.ratedEnergy ?? null

  const inputs = useMemo<EnvelopeInputs>(
    () => ({
      maxSocPercent,
      minSocPercent,
      maxChargePowerKw,
      maxDischargePowerKw,
      targetSocEnabled,
      targetSocPercent,
      trickleChargePowerKw,
      trickleDischargePowerKw,
      ratedEnergyWh,
    }),
    [
      maxSocPercent,
      minSocPercent,
      maxChargePowerKw,
      maxDischargePowerKw,
      targetSocEnabled,
      targetSocPercent,
      trickleChargePowerKw,
      trickleDischargePowerKw,
      ratedEnergyWh,
    ],
  )

  const showTrickleLegend = targetSocEnabled === true

  // No inner `position: sticky` — the OUTER sidebar container in ResourceLocalConfigForm already
  // pins the whole right column. Stacking sticky-on-sticky bumped the chart's heading off-screen
  // while leaving the SVG visible; keeping the chart heading as part of the normal flow inside the
  // already-sticky sidebar makes the heading travel with the SVG as the user scrolls the form.
  return (
    <Paper data-testid="envelope-card" sx={{ p: 2 }}>
      <Stack direction="column" spacing={1.5}>
        <CustomTypography variant="h3">{t('resources.configuration.envelope.title')}</CustomTypography>
        {/* Directly under the title and above the plot: rated energy is what scales the energy axis,
         * so stating it here lets a reader check a surprising axis reading against its cause without
         * leaving the card. Not rendered at all without a battery port — there is no battery to
         * report on, and "not reported yet" would blame one that was never asked. */}
        {batteryControlPortId !== null && (
          <RatedEnergyRow isError={isError} isLoading={isLoading} ratedEnergyWh={ratedEnergyWh} />
        )}
        <OperatingEnvelopeSvg colors={colors} inputs={inputs} />
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip
            label={t('resources.configuration.envelope.legend.operating')}
            size="small"
            sx={{
              backgroundColor: colors.operating.fill,
              border: `1px solid ${colors.operating.stroke}`,
              color: 'text.primary',
            }}
          />
          {showTrickleLegend && (
            <Chip
              label={t('resources.configuration.envelope.legend.trickle')}
              size="small"
              sx={{
                backgroundColor: colors.trickle.fill,
                border: `1px dashed ${colors.trickle.stroke}`,
                color: 'text.primary',
              }}
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

export type EnvelopeInputs = Readonly<{
  maxSocPercent: number | null
  minSocPercent: number | null
  maxChargePowerKw: number | null
  maxDischargePowerKw: number | null
  targetSocEnabled: boolean
  targetSocPercent: number | null
  trickleChargePowerKw: number | null
  trickleDischargePowerKw: number | null
  ratedEnergyWh: number | null
}>

// Fixed drawing width. The viewBox keeps this coordinate system while CSS scales the SVG to the
// column, so it only has to be wide enough for the plot plus both label margins below.
const SVG_WIDTH = 444
const SVG_HEIGHT = 360
// Bottom margin allows 4 rows of tick/value labels (+18, +32, +46, +60 — the last is the
// staggered second row used when close labels collide) plus the X axis title at +78. The whole stack
// sits 4 px lower than it did to clear the new right-hand energy column: the rightmost X tick is
// centred on the plot's right edge and so overhangs into the energy labels' x-range, leaving only a
// ~3 px vertical gutter against the 0 % energy tick before this shift.
//
// Left and right are each derived from the widest label that side actually carries, using this file's
// own `approxTextWidth` rule (5.5 px per character at font-size 10) and the ~11 px band a rotated
// font-size-11 title occupies around its anchor:
//
//   LEFT  — percentage ticks only, widest "100 %" = 5 ch ≈ 28 px, end-anchored at `left - 6` growing
//           leftward. Y title anchored at x=14 occupies x≈[9, 20]. At left=60 the label's left edge is
//           60-6-28 = 26, clearing the title by ~6 px.
//   RIGHT — energy ticks, widest 8 ch ("1000 kWh", "25.0 MWh") ≈ 44 px, start-anchored at plot
//           right + 6. Energy title anchored at SVG_WIDTH-14 occupies x≈[425, 436]. At right=82 an
//           8-ch label ends at 444-82+6+44 = 412, clearing the title by ~13 px. A 9th character IS
//           reachable and the reserve is not spare: rounding up across a decimal tier gives
//           "10.00 kWh" (domain 9 996 Wh) or "100.0 MWh", ending at 417.5 and still ~7 px clear.
//
// Both margins are STATIC: the plot rectangle has the same width whether or not ratedEnergy is known,
// so two resources' envelopes stay visually comparable. The cost is an empty right gutter on a
// battery that has not reported — whitespace in a sidebar card, which is the cheaper of the two evils
// against threading per-render layout through the 46 references to these constants in the SVG body.
const MARGIN = { top: 16, right: 82, bottom: 92, left: 60 }
const PLOT_WIDTH = SVG_WIDTH - MARGIN.left - MARGIN.right
const PLOT_HEIGHT = SVG_HEIGHT - MARGIN.top - MARGIN.bottom
const STAGGER_ROW_HEIGHT = 14
const STAGGER_TOLERANCE_PX = 4
// A step shorter than a 10 px line box cannot hold a direction label at all, so it is dropped rather
// than drawn across the step's own edges.
//
// Height is NOT the only guard, and horizontal separation is not free either: each label is centred
// in its own half of the plot, so a lopsided charge:discharge ratio pulls that centre towards zero
// and the label spills across into the opposite half, where the opposing SoC annotation sits. At
// 50 kW charge / 5 kW discharge the two overlapped by 33 px; at the create-mode 5 %/95 % caps with
// 500 kW / 10 kW, by 2 px. So each direction label is also dropped when it would actually intersect
// the annotation it could collide with — measured, not assumed. See `labelsOverlap`.
const DIRECTION_LABEL_MIN_STEP_PX = 10

/** Approximate text width at the given font size — close enough for label-collision detection
 * without forcing a DOM measure pass while computing positions. */
function approxTextWidth(text: string, fontSize = 10): number {
  return text.length * fontSize * 0.55
}

/** The vertical extent of a font-size-10 label around its baseline, for collision detection. */
const LABEL_LINE_HEIGHT_PX = 10

/** Clamps a power magnitude at zero, preserving `null` (which means "not configured", not "zero").
 * See the call site for why every magnitude goes through this before it reaches the geometry. */
function clampToZero(kw: number | null): number | null {
  return kw === null ? null : Math.max(0, kw)
}

/** A label as positioned in the plot: centre-anchored at (x, y) with this text. */
type PlacedLabel = { x: number; y: number; text: string }

/** Whether two centre-anchored font-size-10 labels would visibly intersect. Used to drop a label
 * rather than draw it over another — both axes matter, since these labels sit in the plot rather
 * than in a row, so being on different y rows is as good as being horizontally clear. */
function labelsOverlap(a: PlacedLabel, b: PlacedLabel): boolean {
  const halfA = approxTextWidth(a.text) / 2
  const halfB = approxTextWidth(b.text) / 2
  const horizontal = Math.min(a.x + halfA, b.x + halfB) - Math.max(a.x - halfA, b.x - halfB) > 0
  return horizontal && Math.abs(a.y - b.y) < LABEL_LINE_HEIGHT_PX
}

/** Decide whether two horizontally-positioned labels (centered at `leftX` / `rightX`) need to be
 * stacked on two rows because their bounding boxes would otherwise overlap. */
function staggeredLabelYs(
  leftX: number,
  rightX: number,
  leftText: string,
  rightText: string,
  baseY: number,
): { leftY: number; rightY: number; staggered: boolean } {
  const leftHalf = approxTextWidth(leftText) / 2
  const rightHalf = approxTextWidth(rightText) / 2
  const leftEdge = leftX + leftHalf
  const rightEdge = rightX - rightHalf
  if (rightEdge - leftEdge < STAGGER_TOLERANCE_PX) {
    return { leftY: baseY, rightY: baseY + STAGGER_ROW_HEIGHT, staggered: true }
  }
  return { leftY: baseY, rightY: baseY, staggered: false }
}

/** Pure presentational SVG body. Exported for tests; never reads form state directly.
 *
 * All in-SVG text labels are localised via useTranslation; formatPower and the tick formatters produce
 * the numeric values with unit symbols. Colours come from the theme-derived CHART_COLORS the
 * outer component builds — pass an explicit `colors` prop or fall back to the default theme
 * (tests that render the SVG directly without a ThemeProvider get MUI's default palette). */
export function OperatingEnvelopeSvg({ inputs, colors }: Readonly<{ inputs: EnvelopeInputs; colors?: ChartColors }>) {
  const { t } = useTranslation()
  const theme = useTheme()
  const c = colors ?? buildChartColors(theme)
  const {
    maxSocPercent,
    minSocPercent,
    maxChargePowerKw: rawMaxChargePowerKw,
    maxDischargePowerKw: rawMaxDischargePowerKw,
    targetSocEnabled,
    targetSocPercent,
    trickleChargePowerKw: rawTrickleChargePowerKw,
    trickleDischargePowerKw: rawTrickleDischargePowerKw,
    ratedEnergyWh,
  } = inputs

  // Every power magnitude is clamped at zero ONCE, here, and the raw values are not used again below.
  // A negative magnitude would flip one sub-rectangle of the stepped shape across xZero, and with
  // `fill-rule: nonzero` the overlap cancels to winding 0 — an unfilled but outlined hole claiming
  // discharge is forbidden across the normal SoC band. Clamping inside the path helper alone was not
  // enough: the dashed trickle lines and every value label read the same four numbers, so a negative
  // drew a line 44 px on the WRONG side of zero (labelled "Trickle charge -0.50 MW") while the fill
  // collapsed onto zero — the fill/line contradiction the shape exists to prevent.
  //
  // Reachable, though only briefly: `defaultValues` come straight from the stored resource.json and
  // `parseActivePowerToKw` accepts a leading minus, so a negative arrives with the first paint. It is
  // NOT reachable by typing — `NumericField` passes `min={0}` to `FloatTextField`, which substitutes
  // the bound before form state ever sees the value, and clamps the stored one on mount.
  const maxChargePowerKw = clampToZero(rawMaxChargePowerKw)
  const maxDischargePowerKw = clampToZero(rawMaxDischargePowerKw)
  const trickleChargePowerKw = clampToZero(rawTrickleChargePowerKw)
  const trickleDischargePowerKw = clampToZero(rawTrickleDischargePowerKw)

  // Symmetric X domain around 0 with 15% headroom so the largest rectangle never touches the edge.
  const maxAbsKw = Math.max(
    maxChargePowerKw ?? 0,
    maxDischargePowerKw ?? 0,
    targetSocEnabled === true ? (trickleChargePowerKw ?? 0) : 0,
    targetSocEnabled === true ? (trickleDischargePowerKw ?? 0) : 0,
    100,
  )
  const xDomainKw = maxAbsKw * 1.15

  // Choose MW vs kW for the X-axis labels — MW once we're past 1 MW.
  const useMw = xDomainKw >= 1000
  const xUnit = useMw ? 'MW' : 'kW'
  const xUnitFactor = useMw ? 1 / 1000 : 1
  const formatPower = (kw: number) => {
    const scaled = kw * xUnitFactor
    const fixed = Math.abs(scaled) >= 10 ? scaled.toFixed(0) : scaled.toFixed(2)
    return `${fixed} ${xUnit}`
  }

  // Energy labels only when rated energy is known; the unit follows the battery's magnitude, since
  // hardcoding MWh rendered NW-scale batteries as "0.02 MWh" — technically right, unreadable.
  //
  // NOTE this does NOT share a mechanism with `formatPower` above, despite doing the same job: that
  // one scales inline with a hardcoded 1000, literal unit strings and a two-tier decimal rule, while
  // this one goes through `formatEnergyWh` with named constants and three tiers. Unifying them would
  // change X-axis precision, so it is deliberately left for a follow-up.
  //
  // `isReportedEnergyWh` narrows, so the labels below need no `?? 0` fallback — and shouldn't have
  // one, since a fallback would quietly render "0.00 MWh" if the guard were ever weakened.
  const showEnergyAxis = isReportedEnergyWh(ratedEnergyWh)
  const formatPercentTick = (percent: number) => `${percent.toFixed(0)} %`
  const formatEnergyTick = (percent: number) =>
    showEnergyAxis ? formatEnergyWh((ratedEnergyWh * percent) / 100, ratedEnergyWh) : null
  // The setpoint annotation keeps BOTH values on one line: it marks a single point inside the plot and
  // states the two facts about it, so it is not a tick on either axis and does not belong to either
  // margin.
  const formatSetpointLabel = (percent: number) => {
    const energy = formatEnergyTick(percent)
    return energy === null ? formatPercentTick(percent) : `${energy} · ${formatPercentTick(percent)}`
  }

  const xScale = (kw: number) => MARGIN.left + ((kw + xDomainKw) / (2 * xDomainKw)) * PLOT_WIDTH
  const yScale = (percent: number) => MARGIN.top + PLOT_HEIGHT - (percent / 100) * PLOT_HEIGHT

  /**
   * The permitted region as ONE stepped polygon, walked from zero power at 0 % round to the start.
   *
   * The SoC caps are DIRECTIONAL, which a single min-to-max rectangle cannot express: below min SoC
   * charging is not merely allowed but encouraged, and above max SoC discharging still is. So the
   * charge (right) edge spans 0 % up to max SoC, the discharge (left) edge spans min SoC up to 100 %,
   * and the two steps between them are the shape's whole point.
   *
   * Called TWICE — once with the power limits for the envelope, once with ±trickle for the deadband.
   * The deadband is directional for exactly the same reason, so it is the same shape at a different
   * scale rather than a rectangle between the caps. Drawing it as a rectangle makes the trickle LINES
   * (which are clipped by direction) and the trickle FILL assert different things about one limit.
   *
   * Degenerates without special-casing: `minSocPercent` 0 drops the bottom step, `maxSocPercent`
   * 100 drops the top, and both together give exactly the old full-height rectangle.
   *
   * ASSUMPTIONS, approved on the mockup but NOT yet confirmed with the domain expert (see LC-416,
   * which records who owes the confirmation):
   *  1. The end regions run to the FULL charge / discharge limit, not a reduced one.
   *  2. Trickle does not apply in the direction that is disallowed — hence the clipped lines.
   *  3. The regions reach 0 % and 100 %; no harder BMS floor or ceiling is modelled.
   *  4. Symmetric: discharge above max SoC at the full discharge limit mirrors charge below min SoC.
   */
  const steppedEnvelopePath = (minPct: number, maxPct: number, chargeKw: number, dischargeKw: number) => {
    // Both magnitudes are already clamped at zero by the caller — see `clampToZero` at the top of
    // this component for why, and why clamping here alone was not enough.
    const xZero = xScale(0)
    const xCharge = xScale(chargeKw)
    const xDischarge = xScale(-dischargeKw)
    const yBottom = yScale(0)
    const yTop = yScale(100)
    const yMin = yScale(minPct)
    const yMax = yScale(maxPct)

    // `dischargeKw` is a positive magnitude, negated here; `chargeKw` is used as given.
    return `${[
      [xZero, yBottom],
      [xCharge, yBottom],
      [xCharge, yMax],
      [xZero, yMax],
      [xZero, yTop],
      [xDischarge, yTop],
      [xDischarge, yMin],
      [xZero, yMin],
    ]
      .map(([px, py], index) => `${index === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`)
      .join(' ')} Z`
  }

  // Y-axis label rows: 0%, 25%, 50%, 75%, 100%
  const yTicks = [0, 25, 50, 75, 100]
  // X-axis ticks: −xDomain, −xDomain/2, 0, +xDomain/2, +xDomain
  const xTicks = [-xDomainKw, -xDomainKw / 2, 0, xDomainKw / 2, xDomainKw]

  const hasOperatingRect =
    typeof maxSocPercent === 'number' &&
    typeof minSocPercent === 'number' &&
    typeof maxChargePowerKw === 'number' &&
    typeof maxDischargePowerKw === 'number'

  const hasTrickleRect =
    targetSocEnabled === true &&
    typeof maxSocPercent === 'number' &&
    typeof minSocPercent === 'number' &&
    typeof trickleChargePowerKw === 'number' &&
    typeof trickleDischargePowerKw === 'number'

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Responsive sizing: `width: 100%` + `height: auto` lets the SVG stretch to the column width
       * while the viewBox keeps the internal coordinate system (MARGIN.left etc.) fixed, so the chart
       * fills the horizontal space and its height follows the aspect ratio — no letterbox whitespace. */}
      <svg
        aria-label={t('resources.configuration.envelope.aria_label')}
        role="img"
        style={{ display: 'block', width: '100%', height: 'auto' }}
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
      >
        {/* Faint background grid */}
        {yTicks.map((tick) => (
          <line
            key={`grid-y-${tick}`}
            stroke={c.grid}
            strokeDasharray="2 2"
            x1={MARGIN.left}
            x2={MARGIN.left + PLOT_WIDTH}
            y1={yScale(tick)}
            y2={yScale(tick)}
          />
        ))}
        {xTicks.map((tick) => (
          <line
            key={`grid-x-${tick}`}
            stroke={c.grid}
            strokeDasharray="2 2"
            x1={xScale(tick)}
            x2={xScale(tick)}
            y1={MARGIN.top}
            y2={MARGIN.top + PLOT_HEIGHT}
          />
        ))}

        {/* Left-hand ticks: percentage only, under the "State of charge" title. */}
        {yTicks.map((tick) => (
          <text
            key={`y-label-${tick}`}
            dominantBaseline="middle"
            fill={c.axisLabel}
            fontSize="10"
            textAnchor="end"
            x={MARGIN.left - 6}
            y={yScale(tick)}
          >
            {formatPercentTick(tick)}
          </text>
        ))}

        {/* Right-hand ticks: the same rows in energy, under the "Energy" title, which previously
         * labelled an empty margin while both values crowded the left. */}
        {showEnergyAxis &&
          yTicks.map((tick) => (
            <text
              key={`energy-label-${tick}`}
              data-testid={`envelope-energy-tick-${tick}`}
              dominantBaseline="middle"
              fill={c.axisLabel}
              fontSize="10"
              textAnchor="start"
              x={MARGIN.left + PLOT_WIDTH + 6}
              y={yScale(tick)}
            >
              {formatEnergyTick(tick)}
            </text>
          ))}

        {/* X-axis labels */}
        {xTicks.map((tick) => (
          <text
            key={`x-label-${tick}`}
            fill={c.axisLabel}
            fontSize="10"
            textAnchor="middle"
            x={xScale(tick)}
            y={MARGIN.top + PLOT_HEIGHT + 18}
          >
            {formatPower(tick)}
          </text>
        ))}

        {/* Axis lines */}
        <line
          stroke={c.axisLine}
          x1={MARGIN.left}
          x2={MARGIN.left + PLOT_WIDTH}
          y1={MARGIN.top + PLOT_HEIGHT}
          y2={MARGIN.top + PLOT_HEIGHT}
        />
        <line stroke={c.axisLine} x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + PLOT_HEIGHT} />
        {/* Zero-power vertical line — same divider ink as the grid; sits between gridline and
         * axis-line in visual weight. */}
        <line stroke={c.grid} x1={xScale(0)} x2={xScale(0)} y1={MARGIN.top} y2={MARGIN.top + PLOT_HEIGHT} />

        {/* Axis titles — rendered always for X / Y; Energy gated on ratedEnergy. */}
        {(() => {
          const yMid = MARGIN.top + PLOT_HEIGHT / 2
          const xMid = MARGIN.left + PLOT_WIDTH / 2
          return (
            <>
              <text
                data-testid="envelope-y-axis-title"
                fill={c.axisTitle}
                fontSize="11"
                textAnchor="middle"
                transform={`rotate(-90 14 ${yMid})`}
                x={14}
                y={yMid}
              >
                {t('resources.configuration.envelope.y_axis')}
              </text>
              <text
                data-testid="envelope-x-axis-title"
                fill={c.axisTitle}
                fontSize="11"
                textAnchor="middle"
                x={xMid}
                y={MARGIN.top + PLOT_HEIGHT + 78}
              >
                {t('resources.configuration.envelope.x_axis')}
              </text>
              {showEnergyAxis && (
                <text
                  data-testid="envelope-energy-axis-title"
                  fill={c.axisTitle}
                  fontSize="11"
                  textAnchor="middle"
                  transform={`rotate(90 ${SVG_WIDTH - 14} ${yMid})`}
                  x={SVG_WIDTH - 14}
                  y={yMid}
                >
                  {t('resources.configuration.envelope.energy_axis')}
                </text>
              )}
            </>
          )
        })()}

        {/* Permitted region — one stepped polygon, one fill. The step IS the meaning; see
         * `steppedEnvelopePath` for why a min-to-max rectangle under-draws the real envelope. */}
        {hasOperatingRect &&
          (() => {
            const dischargeText = t('resources.configuration.envelope.max_discharge_label', {
              value: formatPower(maxDischargePowerKw),
            })
            const chargeText = t('resources.configuration.envelope.max_charge_label', {
              value: formatPower(maxChargePowerKw),
            })
            // SIGNED, not `Math.abs`. SVG y grows downward, so a real top step gives
            // `yScale(maxSoc) - yScale(100) > 0`; inverting the operands suppresses the label forever,
            // which is what the approved mockup did. But `Math.abs` is worse than either: it makes an
            // INVERTED step (maxSoc > 100, or a negative minSoc from stored config) look like a valid
            // positive one and places the label outside the plot. Signed keeps the order and rejects
            // the inversion. No `?? 0` fallbacks — both are narrowed here.
            const topStepHeightPx = yScale(maxSocPercent) - yScale(100)
            const bottomStepHeightPx = yScale(0) - yScale(minSocPercent)

            // Each annotation is centred over the half it caps; each direction label over the half
            // where its direction is NOT allowed. Both are computed here so the collision test below
            // measures the positions actually rendered.
            const dischargeHalfCentreX = (xScale(-maxDischargePowerKw) + xScale(0)) / 2
            const chargeHalfCentreX = (xScale(0) + xScale(maxChargePowerKw)) / 2
            const maxSocAnnotation: PlacedLabel = {
              x: dischargeHalfCentreX,
              y: yScale(maxSocPercent) - 4,
              text: t('resources.configuration.envelope.max_soc_label', { percent: maxSocPercent.toFixed(0) }),
            }
            const minSocAnnotation: PlacedLabel = {
              x: chargeHalfCentreX,
              y: yScale(minSocPercent) + 12,
              text: t('resources.configuration.envelope.min_soc_label', { percent: minSocPercent.toFixed(0) }),
            }
            const chargeOnlyLabel: PlacedLabel = {
              x: dischargeHalfCentreX,
              y: (yScale(minSocPercent) + yScale(0)) / 2 + 3,
              text: t('resources.configuration.envelope.charge_only'),
            }
            const dischargeOnlyLabel: PlacedLabel = {
              x: chargeHalfCentreX,
              y: (yScale(100) + yScale(maxSocPercent)) / 2 + 3,
              text: t('resources.configuration.envelope.discharge_only'),
            }
            // An inverted pair is not rejected upstream: the schema's `lessThan` refine only produces
            // a message, and stored config is never validated at all. The shape then degenerates to
            // two disjoint lobes — charge below max SoC, discharge above min SoC — with the band
            // between them permitting nothing. Drawing "charge only" in the middle of that band would
            // state the opposite of the truth, so the direction labels go; the shape stays, because
            // blanking the whole preview on a keystroke is worse than an honest empty band, and the
            // field is already carrying its own error.
            const capsOrdered = minSocPercent < maxSocPercent
            const showChargeOnly =
              capsOrdered &&
              bottomStepHeightPx > DIRECTION_LABEL_MIN_STEP_PX &&
              !labelsOverlap(chargeOnlyLabel, minSocAnnotation)
            const showDischargeOnly =
              capsOrdered &&
              topStepHeightPx > DIRECTION_LABEL_MIN_STEP_PX &&
              !labelsOverlap(dischargeOnlyLabel, maxSocAnnotation)
            const opStagger = staggeredLabelYs(
              xScale(-maxDischargePowerKw),
              xScale(maxChargePowerKw),
              dischargeText,
              chargeText,
              MARGIN.top + PLOT_HEIGHT + 32,
            )
            return (
              <>
                <path
                  d={steppedEnvelopePath(minSocPercent, maxSocPercent, maxChargePowerKw, maxDischargePowerKw)}
                  data-testid="envelope-operating-shape"
                  fill={c.operating.fill}
                  stroke={c.operating.stroke}
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                />
                <text
                  fill={c.operating.label}
                  fontSize="10"
                  textAnchor="middle"
                  x={maxSocAnnotation.x}
                  y={maxSocAnnotation.y}
                >
                  {maxSocAnnotation.text}
                </text>
                <text
                  fill={c.operating.label}
                  fontSize="10"
                  textAnchor="middle"
                  x={minSocAnnotation.x}
                  y={minSocAnnotation.y}
                >
                  {minSocAnnotation.text}
                </text>
                {/* Direction labels, each in the half of its step where that direction is NOT allowed —
                 * the only free space. Below min SoC only charging is permitted, so the left half is
                 * empty and carries "charge only"; above max SoC the right half carries "discharge
                 * only". Dropped when the step is too short to hold them, when they would land on the
                 * opposing SoC annotation, or when the caps are inverted — see the guards above. */}
                {showChargeOnly && (
                  <text
                    data-testid="envelope-charge-only-label"
                    fill={c.operating.label}
                    fontSize="10"
                    textAnchor="middle"
                    x={chargeOnlyLabel.x}
                    y={chargeOnlyLabel.y}
                  >
                    {chargeOnlyLabel.text}
                  </text>
                )}
                {showDischargeOnly && (
                  <text
                    data-testid="envelope-discharge-only-label"
                    fill={c.operating.label}
                    fontSize="10"
                    textAnchor="middle"
                    x={dischargeOnlyLabel.x}
                    y={dischargeOnlyLabel.y}
                  >
                    {dischargeOnlyLabel.text}
                  </text>
                )}
                <text
                  data-testid="envelope-max-discharge-label"
                  fill={c.operating.label}
                  fontSize="10"
                  textAnchor="middle"
                  x={xScale(-maxDischargePowerKw)}
                  y={opStagger.leftY}
                >
                  {dischargeText}
                </text>
                <text
                  data-testid="envelope-max-charge-label"
                  fill={c.operating.label}
                  fontSize="10"
                  textAnchor="middle"
                  x={xScale(maxChargePowerKw)}
                  y={opStagger.rightY}
                >
                  {chargeText}
                </text>
              </>
            )
          })()}

        {/* Trickle range (dashed coral) */}
        {hasTrickleRect &&
          (() => {
            const dischargeText = t('resources.configuration.envelope.trickle_discharge_label', {
              value: formatPower(trickleDischargePowerKw),
            })
            const chargeText = t('resources.configuration.envelope.trickle_charge_label', {
              value: formatPower(trickleChargePowerKw),
            })
            const trStagger = staggeredLabelYs(
              xScale(-trickleDischargePowerKw),
              xScale(trickleChargePowerKw),
              dischargeText,
              chargeText,
              MARGIN.top + PLOT_HEIGHT + 46,
            )
            return (
              <>
                {/* SAME shape as the envelope, at ±trickle. Not a rectangle between the caps: the
                 * deadband is directional for the same reason the envelope is, and drawing it as a
                 * rectangle would have the fill and the clipped lines below contradict each other
                 * about one limit. Fill only — the two dashed lines carry the boundary. */}
                <path
                  d={steppedEnvelopePath(minSocPercent, maxSocPercent, trickleChargePowerKw, trickleDischargePowerKw)}
                  data-testid="envelope-trickle-shape"
                  fill={c.trickle.fill}
                />
                {/* Each line spans only where its direction is permitted: charge from 0 % to max SoC,
                 * discharge from min SoC to 100 %. */}
                <line
                  data-testid="envelope-trickle-charge-line"
                  stroke={c.trickle.stroke}
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  x1={xScale(trickleChargePowerKw)}
                  x2={xScale(trickleChargePowerKw)}
                  y1={yScale(maxSocPercent)}
                  y2={yScale(0)}
                />
                <line
                  data-testid="envelope-trickle-discharge-line"
                  stroke={c.trickle.stroke}
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  x1={xScale(-trickleDischargePowerKw)}
                  x2={xScale(-trickleDischargePowerKw)}
                  y1={yScale(100)}
                  y2={yScale(minSocPercent)}
                />
                <text
                  data-testid="envelope-trickle-discharge-label"
                  fill={c.trickle.label}
                  fontSize="10"
                  textAnchor="middle"
                  x={xScale(-trickleDischargePowerKw)}
                  y={trStagger.leftY}
                >
                  {dischargeText}
                </text>
                <text
                  data-testid="envelope-trickle-charge-label"
                  fill={c.trickle.label}
                  fontSize="10"
                  textAnchor="middle"
                  x={xScale(trickleChargePowerKw)}
                  y={trStagger.rightY}
                >
                  {chargeText}
                </text>
              </>
            )
          })()}

        {/* SoC setpoint dashed line */}
        {targetSocEnabled === true && typeof targetSocPercent === 'number' && (
          <>
            <line
              data-testid="envelope-setpoint-line"
              stroke={c.trickle.label}
              strokeDasharray="6 4"
              strokeWidth={1.5}
              x1={MARGIN.left}
              x2={MARGIN.left + PLOT_WIDTH}
              y1={yScale(targetSocPercent)}
              y2={yScale(targetSocPercent)}
            />
            <text
              fill={c.trickle.label}
              fontSize="10"
              textAnchor="start"
              x={MARGIN.left + 4}
              y={yScale(targetSocPercent) - 4}
            >
              {t('resources.configuration.envelope.soc_setpoint_label', {
                value: formatSetpointLabel(targetSocPercent),
              })}
            </text>
          </>
        )}
      </svg>
    </Box>
  )
}
